/**
 * Dashboard Aplikasi Terintegrasi — tampilan minimalis, aksen biru / merah / hijau
 */

import { useState, useEffect, useMemo } from "react";
import axios from "axios";

import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import Header from "./components/Header";
import { API_ESA_OFFICE } from "utils";
import logoESA from "assets/images/ESAOFFICE_LOGO.png";
import logorote from "assets/images/logorote.png";

function getAccentKey(index) {
  const k = index % 3;
  if (k === 0) return "info";
  if (k === 1) return "error";
  return "success";
}

function AllApplications() {
  const theme = useTheme();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const logoESAUrl = logoESA || "/ESAOFFICE_LOGO.png";
  const logoroteUrl = logorote || "/logorote.png";

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${API_ESA_OFFICE}aplikasi-terintegrasi`);

        if (response.data.success === 1) {
          setApplications(response.data.data || []);
        } else {
          setError("Gagal mengambil data aplikasi");
          setApplications([]);
        }
      } catch (err) {
        console.error("Error fetching applications:", err);
        setError("Terjadi kesalahan saat mengambil data aplikasi");
        setApplications([]);
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, []);

  const openApplication = (link) => {
    if (link) window.open(link, "_blank", "noopener,noreferrer");
  };

  const formatDateIndonesia = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const applicationCards = useMemo(() => {
    if (!applications.length) return null;
    return applications.map((app, index) => {
      const accent = getAccentKey(index);
      const borderLeftColor =
        accent === "info"
          ? theme.palette.info.main
          : accent === "error"
          ? theme.palette.error.main
          : theme.palette.success.main;

      const deskripsi =
        app.deskripsi ||
        "Aplikasi terintegrasi untuk mendukung layanan digital pemerintahan daerah.";
      const shortDesc = deskripsi.length > 160 ? `${deskripsi.slice(0, 160)}…` : deskripsi;

      return (
        <Grid item xs={12} sm={6} lg={4} key={app.id}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              border: "1px solid",
              borderColor: "grey.200",
              borderRadius: 2,
              borderLeftWidth: 4,
              borderLeftColor,
              bgcolor: "background.paper",
              transition: "box-shadow 0.2s ease, border-color 0.2s ease",
              "&:hover": {
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                borderColor: "grey.300",
              },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column" }}>
              <MDBox
                display="flex"
                gap={1.5}
                alignItems="flex-start"
                sx={{ flex: 1, minWidth: 0, mb: 1.5 }}
              >
                <MDBox
                  flexShrink={0}
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    overflow: "hidden",
                    bgcolor: "grey.50",
                    border: "1px solid",
                    borderColor: "grey.200",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0.75,
                  }}
                >
                  <MDBox
                    component="img"
                    src={logoroteUrl}
                    alt=""
                    sx={{
                      width: "72%",
                      height: "72%",
                      objectFit: "contain",
                    }}
                  />
                </MDBox>
                <MDBox flex={1} minWidth={0} display="flex" flexDirection="column">
                  <MDTypography variant="subtitle2" fontWeight="600" sx={{ mb: 0.75, lineHeight: 1.35 }}>
                    {app.nama_aplikasi}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      mb: 1,
                      lineHeight: 1.55,
                      flex: 1,
                      display: "-webkit-box",
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {shortDesc}
                  </MDTypography>
                  {formatDateIndonesia(app.dibuat) ? (
                    <Chip
                      label={formatDateIndonesia(app.dibuat)}
                      size="small"
                      variant="outlined"
                      color={accent}
                      sx={{
                        alignSelf: "flex-start",
                        height: 22,
                        fontSize: "0.7rem",
                        fontWeight: 500,
                        bgcolor:
                          accent === "info"
                            ? "rgba(25, 118, 210, 0.06)"
                            : accent === "error"
                            ? "rgba(211, 47, 47, 0.06)"
                            : "rgba(46, 125, 50, 0.06)",
                      }}
                    />
                  ) : null}
                </MDBox>
              </MDBox>
              <Button
                fullWidth
                variant="outlined"
                disableElevation
                size="small"
                disabled={!app.link}
                onClick={() => openApplication(app.link)}
                startIcon={
                  <Icon fontSize="small" sx={{ color: "#424242" }}>
                    open_in_new
                  </Icon>
                }
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "0.8125rem",
                  py: 0.5,
                  minHeight: 36,
                  color: "#424242",
                  bgcolor: "transparent",
                  borderWidth: 1,
                  borderStyle: "solid",
                  borderColor: borderLeftColor,
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "rgba(0, 0, 0, 0.04)",
                    borderColor: borderLeftColor,
                    color: "#333333",
                  },
                  "&.Mui-disabled": {
                    bgcolor: "transparent",
                    color: "grey.500",
                    borderColor: "grey.300",
                  },
                }}
              >
                Buka aplikasi
              </Button>
            </CardContent>
          </Card>
        </Grid>
      );
    });
  }, [applications, theme.palette, logoroteUrl]);

  const layoutShell = (children) => (
    <DashboardLayout>
      <DashboardNavbar />
      <Header profile={logoESAUrl} />
      <MDBox pb={3} px={{ xs: 0, sm: 0 }}>
        {children}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );

  if (loading) {
    return layoutShell(
      <MDBox display="flex" justifyContent="center" alignItems="center" py={6} gap={1.5}>
        <CircularProgress size={28} color="info" thickness={4} />
        <MDTypography variant="caption" color="text.secondary">
          Memuat aplikasi…
        </MDTypography>
      </MDBox>
    );
  }

  if (error) {
    return layoutShell(
      <MDBox px={{ xs: 2, md: 3 }}>
        <Card
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "error.light",
            borderLeftWidth: 4,
            borderLeftColor: "error.main",
            bgcolor: "rgba(211, 47, 47, 0.04)",
          }}
        >
          <CardContent>
            <MDBox display="flex" alignItems="center" gap={1}>
              <Icon color="error" sx={{ fontSize: 24 }}>
                error_outline
              </Icon>
              <MDTypography variant="body2" color="error" fontWeight="500">
                {error}
              </MDTypography>
            </MDBox>
          </CardContent>
        </Card>
      </MDBox>
    );
  }

  return layoutShell(
    <MDBox px={{ xs: 2, md: 3 }}>
      <MDBox
        sx={{
          pl: 1.5,
          borderLeft: "4px solid",
          borderColor: "success.main",
          mb: 3,
        }}
      >
        <MDTypography variant="h6" fontWeight="600" color="text">
          Ekosistem kerja digital
        </MDTypography>
        <MDTypography variant="body2" color="text.secondary" sx={{ maxWidth: 720, lineHeight: 1.7 }}>
          ESA OFFICE menghubungkan aplikasi layanan daerah agar data dan proses kerja lebih selaras.
          Pilih kartu di bawah untuk membuka aplikasi di tab baru.
        </MDTypography>
      </MDBox>

      {applications.length === 0 ? (
        <MDBox
          textAlign="center"
          py={6}
          sx={{
            border: "1px dashed",
            borderColor: "grey.300",
            borderRadius: 2,
            bgcolor: "grey.50",
          }}
        >
          <Icon sx={{ fontSize: 40, color: "info.main", opacity: 0.45, mb: 1 }}>
            apps
          </Icon>
          <MDTypography variant="body2" color="text.secondary">
            Belum ada aplikasi terdaftar
          </MDTypography>
        </MDBox>
      ) : (
        <Grid container spacing={2}>
          {applicationCards}
        </Grid>
      )}
    </MDBox>
  );
}

export default AllApplications;
